<!-- src/components/CalendarWidget.vue -->
<template>
  <div class="calendar-widget">
    <header class="widget-header">
      <h3>My Calendar</h3>
      <button @click="showForm = !showForm" class="btn-toggle">
        {{ showForm ? 'Cancel' : '+ New Event' }}
      </button>
    </header>

    <!-- Create Event Form -->
    <form v-if="showForm" @submit.prevent="handleSubmit" class="event-form">
      <div class="form-group">
        <label for="title">Title *</label>
        <input 
          id="title" 
          v-model="form.title" 
          type="text" 
          required 
          placeholder="e.g. Home Inspection" 
        />
      </div>

      <div class="form-group">
        <label for="startTime">Start Date & Time *</label>
        <input 
          id="startTime" 
          v-model="form.startTime" 
          type="datetime-local" 
          required 
        />
      </div>

      <div class="form-group">
        <label for="description">Description</label>
        <textarea 
          id="description" 
          v-model="form.description" 
          rows="2" 
          placeholder="Add notes..."
        ></textarea>
      </div>

      <button type="submit" class="btn-submit" :disabled="loading">
        {{ loading ? 'Saving...' : 'Save Event' }}
      </button>
    </form>

    <!-- Error Banner -->
    <div v-if="error" class="status-message error">
      {{ error }}
    </div>

    <!-- Loading State -->
    <div v-if="loading && !events.length" class="status-message">
      Loading your schedule...
    </div>

    <!-- Events Feed -->
    <ul v-if="events.length" class="events-list">
      <li v-for="event in events" :key="event.id" class="event-card">
        <div class="event-date">
          {{ formatDate(event.startTime) }}
        </div>
        <div class="event-info">
          <h4>{{ event.title }}</h4>
          <p v-if="event.description">{{ event.description }}</p>
        </div>
      </li>
    </ul>

    <!-- Empty State -->
    <div v-else-if="!loading" class="empty-state">
      No upcoming events found.
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useCalendarEvents } from '@/composables/useCalendarEvents';

const { events, loading, error, fetchEvents, addEvent } = useCalendarEvents();

const showForm = ref(false);
const form = reactive({
  title: '',
  description: '',
  startTime: '',
});

// Fetch events from Turso via Vercel /api/events when mounted
onMounted(() => {
  fetchEvents();
});

const handleSubmit = async () => {
  if (!form.title || !form.startTime) return;

  try {
    await addEvent({
      title: form.title,
      description: form.description,
      startTime: form.startTime,
    });

    // Reset form fields
    form.title = '';
    form.description = '';
    form.startTime = '';
    showForm.value = false;
  } catch (err) {
    // Errors are tracked in the composable's `error` ref
  }
};

const formatDate = (isoString) => {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};
</script>

<style lang="scss" scoped>
$primary: #4f46e5;
$border: #e5e7eb;
$text-dark: #111827;
$text-muted: #6b7280;

.calendar-widget {
  background: #ffffff;
  border: 1px solid $border;
  border-radius: 12px;
  padding: 1.5rem;
  max-width: 460px;
  margin: 0 auto;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);

  .widget-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.25rem;

    h3 {
      margin: 0;
      font-size: 1.2rem;
      color: $text-dark;
    }

    .btn-toggle {
      background: transparent;
      border: 1px solid $primary;
      color: $primary;
      border-radius: 6px;
      padding: 0.35rem 0.75rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background: $primary;
        color: #ffffff;
      }
    }
  }

  .event-form {
    background: #f9fafb;
    border: 1px solid $border;
    border-radius: 8px;
    padding: 1rem;
    margin-bottom: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      label {
        font-size: 0.8rem;
        font-weight: 600;
        color: #374151;
      }

      input, textarea {
        padding: 0.5rem;
        border: 1px solid $border;
        border-radius: 6px;
        font-size: 0.9rem;
        outline: none;

        &:focus {
          border-color: $primary;
        }
      }
    }

    .btn-submit {
      background: $primary;
      color: #ffffff;
      border: none;
      border-radius: 6px;
      padding: 0.55rem;
      font-weight: 600;
      cursor: pointer;

      &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }
    }
  }

  .status-message {
    padding: 0.6rem;
    font-size: 0.85rem;
    text-align: center;
    color: $text-muted;

    &.error {
      color: #991b1b;
      background: #fef2f2;
      border-radius: 6px;
      margin-bottom: 1rem;
    }
  }

  .events-list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;

    .event-card {
      display: flex;
      gap: 1rem;
      padding: 0.85rem;
      border: 1px solid $border;
      border-radius: 8px;
      background: #ffffff;

      .event-date {
        font-size: 0.8rem;
        font-weight: 700;
        color: $primary;
        min-width: 95px;
      }

      .event-info {
        h4 {
          margin: 0 0 0.25rem 0;
          font-size: 0.95rem;
          color: $text-dark;
        }

        p {
          margin: 0;
          font-size: 0.85rem;
          color: $text-muted;
        }
      }
    }
  }

  .empty-state {
    text-align: center;
    color: $text-muted;
    font-size: 0.9rem;
    padding: 1rem 0;
  }
}
</style>